/**
 * Filtros e ordenação da lista de Clientes. O estado vive na URL
 * (`?situacao=andamento&filtro=atraso&ordem=risco&dir=desc&q=...`), para o
 * link poder ser compartilhado. Funções puras.
 */
import { normalizeText } from "../../../../utils/data";
import { parseDinheiro } from "../../../../utils/dinheiro";
import { toDate } from "../../../../utils/status";
import { SITUACOES, compararTexto } from "./montarClientes";
import { ESPERA, entregaEmBreve } from "./regras";

export const FILTROS_RAPIDOS = Object.freeze({
  ATRASO: "atraso",
  ENTREGA_PROXIMA: "entrega7",
  ESPERAM_EQUIPE: "equipe",
  COM_PENDENCIAS: "pendencias",
});

/** Filtros rápidos que dependem da Central (quem espera, pendências). */
export const FILTROS_DA_CENTRAL = new Set([FILTROS_RAPIDOS.ESPERAM_EQUIPE, FILTROS_RAPIDOS.COM_PENDENCIAS]);

const REGRA_DO_FILTRO = {
  [FILTROS_RAPIDOS.ATRASO]: (l) => l.atrasado,
  [FILTROS_RAPIDOS.ENTREGA_PROXIMA]: (l) => entregaEmBreve(l.diasProximaEntrega),
  [FILTROS_RAPIDOS.ESPERAM_EQUIPE]: (l) => l.espera === ESPERA.EQUIPE,
  [FILTROS_RAPIDOS.COM_PENDENCIAS]: (l) => l.pendencias > 0,
};

export const ORDENS = Object.freeze({
  RISCO: "risco",
  SAUDE: "saude",
  NOME: "nome",
  AMBIENTES: "ambientes",
  ENTREGA: "entrega",
  PENDENCIAS: "pendencias",
});

/** Chave de cada ordem, no sentido "ascendente". Risco e pendências: maior primeiro. */
const CHAVE_DA_ORDEM = {
  [ORDENS.RISCO]: (l) => -l.risco,
  [ORDENS.SAUDE]: (l) => l.saude,
  [ORDENS.NOME]: (l) => l.nome,
  [ORDENS.AMBIENTES]: (l) => (l.totalAmbientes ? l.entregues / l.totalAmbientes : 1),
  [ORDENS.ENTREGA]: (l) => (l.diasProximaEntrega ?? Number.POSITIVE_INFINITY),
  [ORDENS.PENDENCIAS]: (l) => -l.pendencias,
};

export const FILTROS_AVANCADOS_INICIAIS = Object.freeze({
  faixa: "",
  vendedor: "",
  consultor: "",
  obraFutura: "",
  valorMin: "",
  valorMax: "",
  atrasoMin: "",
  atrasoMax: "",
  pedidosMin: "",
  pedidosMax: "",
  entregaDe: "",
  entregaAte: "",
});

function numero(valor) {
  if (valor === "" || valor == null) return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
}

function dentroDaFaixa(valor, min, max, ler = numero) {
  const de = ler(min);
  const ate = ler(max);
  return (de === null || valor >= de) && (ate === null || valor <= ate);
}

function dataNoIntervalo(valor, de, ate) {
  const data = toDate(valor);
  if (!data) return false;
  data.setHours(0, 0, 0, 0);
  if (de && data < new Date(`${de}T00:00:00`)) return false;
  if (ate && data > new Date(`${ate}T00:00:00`)) return false;
  return true;
}

/** Quantos filtros avançados estão ligados (o número no botão "Filtros"). */
export function contarFiltrosAvancados(filtros) {
  return Object.entries(FILTROS_AVANCADOS_INICIAIS).filter(([chave, vazio]) => (filtros[chave] ?? vazio) !== vazio).length;
}

function passaNosAvancados(linha, f) {
  const c = linha.cliente;
  if (f.faixa && linha.faixa !== f.faixa) return false;
  if (f.vendedor && !c.vendedores.includes(f.vendedor)) return false;
  if (f.consultor && !c.consultores.includes(f.consultor)) return false;
  if (f.obraFutura === "sim" && c.obrasFuturas === 0) return false;
  if (f.obraFutura === "nao" && c.obrasFuturas > 0) return false;
  if (!dentroDaFaixa(c.valor, f.valorMin, f.valorMax, parseDinheiro)) return false;
  if (!dentroDaFaixa(linha.atrasoAcumulado, f.atrasoMin, f.atrasoMax)) return false;
  if (!dentroDaFaixa(linha.totalPedidos, f.pedidosMin, f.pedidosMax)) return false;
  if ((f.entregaDe || f.entregaAte) && !dataNoIntervalo(linha.proximaEntrega, f.entregaDe, f.entregaAte)) return false;
  return true;
}

export function filtrarLinhas(linhas, { situacao = SITUACOES.EM_ANDAMENTO, rapido = "", busca = "", avancados = null }) {
  const termo = normalizeText(busca).toLowerCase().trim();
  const regra = REGRA_DO_FILTRO[rapido];
  return linhas.filter((l) => {
    if (situacao !== SITUACOES.TODOS && l.situacao !== situacao) return false;
    if (regra && !regra(l)) return false;
    if (termo && !l.busca.includes(termo)) return false;
    if (avancados && !passaNosAvancados(l, avancados)) return false;
    return true;
  });
}

/** `direcao` 1 = sentido natural da ordem; -1 = invertido. Empate: nome. */
export function ordenarLinhas(linhas, { ordem = ORDENS.RISCO, direcao = 1 } = {}) {
  const chave = CHAVE_DA_ORDEM[ordem] || CHAVE_DA_ORDEM[ORDENS.RISCO];
  return [...linhas].sort((a, b) => {
    const ka = chave(a);
    const kb = chave(b);
    const cmp = typeof ka === "string" ? compararTexto(ka, kb) : ka - kb;
    return cmp * direcao || compararTexto(a.nome, b.nome);
  });
}

/** Contagem por filtro rápido dentro da situação escolhida (os números dos chips). */
export function contarPorFiltroRapido(linhas, situacao) {
  const daSituacao = situacao === SITUACOES.TODOS ? linhas : linhas.filter((l) => l.situacao === situacao);
  return Object.fromEntries(Object.entries(REGRA_DO_FILTRO).map(([id, regra]) => [id, daSituacao.filter(regra).length]));
}
